// Dashboard.js
import React, { useEffect } from 'react';
import axios from 'axios';
import { Select, Typography, DatePicker, InputNumber, Empty } from 'antd';
import { BASE_URL } from './config';
import Header from './components/Header';

import { useMemo, useState } from 'react';
import {
    MaterialReactTable,
    useMaterialReactTable,
} from 'material-react-table';
import { IconButton, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    keepPreviousData,
    useQuery,
} from '@tanstack/react-query';

const styles = {
    wrapper: {
        padding: "20px 50px",
        display: "flex",
        flexDirection: "row",
        gap: "40px",
    },
    gap: {
        display: "flex",
        flexDirection: "row",
        gap: "10px"
    },
    wrap: {
        flexWrap: "wrap"
    },
    stickyTop: {
        background: "#fff",
        position: "sticky",
        top: 0,
        width: "100%",
        zIndex: 1049,
    }
}

export const Dashboard = () => {
    const [tokens, setTokens] = useState([]);
    const [selectedToken, setSelectedToken] = useState(null);
    const [datetimeValue, setDatetimeValue] = useState(null);
    const [gapInMins, setGapInMins] = useState(1);
    const [loginStatus, setLoginStatus] = useState(false);

    useEffect(() => {
        async function init() {
            // Fetch token options (example URL)
            const loginres = await axios.post(BASE_URL + '/api/coins.php', { type: "authenticate" }, { withCredentials: true });
            if (loginres.data.status) {
                setLoginStatus(true);
                axios.post(BASE_URL + '/api/coins.php', { type: "top80" }, { withCredentials: true })
                    .then(response => {
                        const options = response.data.coins.map(token => ({
                            label: `${token.rank}. ${token.name} [${token.symbol}]`,
                            value: token.id,
                        }));
                        setTokens(options);
                    })
                    .catch(error => console.error('Error fetching tokens:', error));
            } else {
                setLoginStatus(false);
            }

        };


        init();
    }, []);

    useEffect(() => {
        if (loginStatus) {
            axios.post(BASE_URL + '/api/coins.php', { type: "top80" }, { withCredentials: true })
                .then(response => {
                    const options = response.data.coins.map(token => ({
                        label: `${token.rank}. ${token.name} [${token.symbol}]`,
                        value: token.id,
                    }));
                    setTokens(options);
                })
                .catch(error => console.error('Error fetching tokens:', error));
        }
    }, [loginStatus])

    const cols = useMemo(
        () => [
            {
                header: 'S.No',
                accessorKey: 'index',
                sortable: true,
                selected: true,
                size: 140
            },
            {
                header: 'Date & Time',
                accessorKey: 'timestamp',
                sortable: true,
                selected: true,
            },
            {
                header: 'Token Value',
                accessorKey: 'tokenValue',
                sortable: true,
                selected: true,
            },
            {
                header: 'Market Cap',
                accessorKey: 'marketCap',
                sortable: true,
                selected: true,
            },
            {
                header: 'Volume (24h)',
                accessorKey: 'volume',
                sortable: true,
                selected: true,
            },
            {
                header: 'Volume/Market Cap (24h)',
                accessorKey: 'volMarket',
                sortable: true,
                selected: true,
            },
            {
                header: 'Addresses by Time Held (Holders >1y)',
                accessorKey: 'holdersPercent',
                sortable: true,
                selected: true,
            },
            {
                header: 'Addresses by Time Held (Cruisers 1-12m)',
                accessorKey: 'cruisersPercent',
                sortable: true,
                selected: true,
            },
            {
                header: 'Addresses by Time Held (Traders <1m)',
                accessorKey: 'tradersPercent',
                sortable: true,
                selected: true,
            },
            {
                header: 'Whale Holdings >1%',
                accessorKey: 'whalesPercent',
                sortable: true,
                selected: true,
            },
            {
                header: '"The Rest" Holdings',
                accessorKey: 'othersPercent',
                sortable: true,
                selected: true,
            },
        ], []
    );

    const {
        data: { data = [], meta } = {}, //your data and api response will probably be different
        isError,
        isRefetching,
        isLoading,
        refetch,
    } = useQuery({
        queryKey: [
            'table-data',
            selectedToken, //refetch when columnFilters changes
            datetimeValue, //refetch when globalFilter changes
            gapInMins, //refetch when pagination.pageIndex changes
        ],
        queryFn: async () => {
            const fetchURL = new URL(
                '/api/coins.php',
                BASE_URL,
            );

            var now = new Date();
            var timezoneOffset = now.getTimezoneOffset();


            //use whatever fetch library you want, fetch, axios, etc
            const response = await fetch(fetchURL.href, { body: JSON.stringify({ type: "coin_detail", end_time: datetimeValue, gap_in_mins: gapInMins, coinId: selectedToken, timezoneOffset: timezoneOffset }), method: "post", credentials: 'include' });
            const json = await response.json();
            // setData(json.data.records)
            console.log(json)
            return { data: json.records, meta: { totalRowCount: json.records.length } };
        },
        placeholderData: keepPreviousData, //don't go to 0 rows when refetching or paginating to next page
    });

    const table = useMaterialReactTable({
        columns: cols,
        data,
        initialState: { showColumnFilters: false, density: 'compact' },
        muiToolbarAlertBannerProps: isError
            ? {
                color: 'error',
                children: 'Error loading data',
            }
            : undefined,
        renderTopToolbarCustomActions: () => (
            <Tooltip arrow title="Refresh Data">
                <IconButton onClick={() => refetch()}>
                    <RefreshIcon />
                </IconButton>
            </Tooltip>
        ),
        rowCount: meta?.totalRowCount ?? 0,
        state: {
            isLoading,
            showAlertBanner: isError,
            showProgressBars: isRefetching,
        },
        enableStickyHeader: true,
        enableColumnOrdering: true,
        enableColumnResizing: true,
        enableStickyFooter: false,
        enableTableFooter: false,
        enablePagination: false,
        enableRowVirtualization: true,
        enableColumnVirtualization: true,
        rowVirtualizerOptions: { overscan: 5 }, //optionally customize the row virtualizer
        columnVirtualizerOptions: { overscan: 2 }, //optionally customize the column virtualizer
        muiTableContainerProps: { sx: { maxHeight: '500px' } },
        enableBottomToolbar:false
    });

    return (
        <>

            <div style={styles.stickyTop}><Header loginStatus={loginStatus} setLoginStatus={setLoginStatus} />
                <div style={styles.wrapper}>
                    <div style={styles.gap}>
                        <div>
                            <Typography.Title level={5}>Select Coin</Typography.Title>
                            <Select
                                showSearch
                                options={tokens}
                                value={selectedToken}
                                onChange={(v) => { setSelectedToken(v) }}
                                placeholder="Select a coin"
                                popupMatchSelectWidth={false}
                                optionFilterProp="label"
                            />
                        </div>

                        <div>
                            <Typography.Title level={5}>Gap (in Mins)</Typography.Title>
                            <InputNumber min={1} defaultValue={1} onChange={(v) => { setGapInMins(v) }} changeOnWheel />
                        </div>


                        <div>
                            <Typography.Title level={5}>Last Time</Typography.Title>
                            <DatePicker
                                showTime
                                showNow
                                onChange={(v, d) => { setDatetimeValue(d) }}
                            />
                        </div>

                    </div>
                </div >
            </div>
            <div style={{ ...styles.wrapper, justifyContent: 'center' }}>
                {(loginStatus) ? (selectedToken) ? <MaterialReactTable table={table} /> : <Empty description={"Select any Coin"} /> : <Empty description={"Login to Use Application"} />}
            </div>
        </>
    );
};

export default Dashboard;
